<template>
  <div
    class="w-full h-full bg-slate-50/70 dark:bg-[#0B1120] rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-slate-200/80 dark:border-[#263449]"
  >
    <!-- Header Section -->
    <div class="mb-6">
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <!-- Small Heading (Left) -->
        <div class="flex items-center gap-2.5">
          <div
            class="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400"
          >
            <BarChart3 class="w-4 h-4" />
          </div>
          <div>
            <h2
              class="text-xs sm:text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider"
            >
              Attendance & Hours Analytics
            </h2>
            <p class="text-[11px] text-slate-400 dark:text-slate-500 font-normal">
              Tracking attendance records and cumulative working time
            </p>
          </div>
        </div>

        <!-- Filters (Right) -->
        <div
          class="flex flex-col sm:flex-row sm:items-center gap-2.5 w-full lg:w-auto"
        >
          <!-- Date Range Dropdown -->
          <div class="relative w-full sm:w-48">
            <select
              v-model="dateRangeOption"
              @change="handleDateRangeChange"
              class="w-full px-3.5 py-2 border border-slate-300 dark:border-[#263449] rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 bg-white dark:bg-[#18243A] text-slate-900 dark:text-white text-xs font-semibold transition"
            >
              <option
                v-for="option in dateRangeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Year Input (Only Full Year & Custom Range) -->
          <div
            v-if="dateRangeOption === 'full' || dateRangeOption === 'custom'"
            class="flex items-center gap-2 bg-white dark:bg-[#18243A] px-3.5 py-1.5 rounded-xl border border-slate-300 dark:border-[#263449] w-full sm:w-auto"
          >
            <label class="text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
              Year:
            </label>
            <input
              v-model.number="selectedYearLocal"
              type="number"
              class="w-full sm:w-16 px-1 py-0.5 border-0 bg-transparent text-xs font-bold text-slate-800 dark:text-white focus:outline-none"
            />
          </div>
        </div>
      </div>

      <!-- Custom Date Range -->
      <div
        v-if="dateRangeOption === 'custom'"
        class="flex flex-col sm:flex-row gap-3 mt-3 pt-3 border-t border-slate-200/60 dark:border-[#1E293B]"
      >
        <input
          v-model="startDate"
          type="date"
          class="w-full px-3 py-2 min-h-[42px] border border-slate-300 dark:border-[#263449] rounded-xl focus:ring-2 focus:ring-indigo-500/20 text-xs sm:text-sm font-semibold bg-white dark:bg-[#18243A] text-slate-900 dark:text-white"
        />
        <input
          v-model="endDate"
          type="date"
          class="w-full px-3 py-2 min-h-[42px] border border-slate-300 dark:border-[#263449] rounded-xl focus:ring-2 focus:ring-indigo-500/20 text-xs sm:text-sm font-semibold bg-white dark:bg-[#18243A] text-slate-900 dark:text-white"
        />
      </div>
    </div>

    <!-- Summary Cards Grid (Matching Reference Image) -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      <!-- Present -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Present
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Present
          </h4>
          <p class="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.presentDays ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Days Present</p>
      </div>

      <!-- Absent -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-rose-50 dark:bg-rose-950/40 rounded-xl text-rose-600 dark:text-rose-400">
              <XCircle class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Absent
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Absent
          </h4>
          <p class="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.absentDays ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Days Absent</p>
      </div>

      <!-- Late -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-amber-50 dark:bg-amber-950/40 rounded-xl text-amber-600 dark:text-amber-400">
              <Clock class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Late
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Late
          </h4>
          <p class="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.lateDays ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Late arrivals</p>
      </div>

      <!-- Total Hours -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-blue-600 dark:text-blue-400">
              <Briefcase class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Hours
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Total Hours
          </h4>
          <p class="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.totalWorkingHours ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Hours logged</p>
      </div>

      <!-- Avg Check-In -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl text-indigo-600 dark:text-indigo-400">
              <LogIn class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Morning
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Avg Check-In
          </h4>
          <p class="text-base sm:text-lg font-bold font-mono text-slate-900 dark:text-white mt-1 tracking-tight truncate">
            {{ attendanceData?.summary?.averageCheckIn ?? "—" }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Entry time</p>
      </div>

      <!-- Avg Check-Out -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-purple-50 dark:bg-purple-950/40 rounded-xl text-purple-600 dark:text-purple-400">
              <LogOut class="w-4 h-4" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Evening
            </span>
          </div>
          <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100">
            Avg Check-Out
          </h4>
          <p class="text-base sm:text-lg font-bold font-mono text-slate-900 dark:text-white mt-1 tracking-tight truncate">
            {{ attendanceData?.summary?.averageCheckOut ?? "—" }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Departure time</p>
      </div>
    </div>

    <!-- Charts Section -->
    <div
      v-if="attendanceData?.monthlyStats"
      class="grid grid-cols-1 lg:grid-cols-2 gap-5"
    >
      <!-- Stacked Attendance Bar Chart -->
      <div class="bg-white dark:bg-[#111C30] rounded-2xl p-5 border border-slate-200/80 dark:border-[#263449] shadow-xs">
        <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <span>Attendance Distribution</span>
          </h3>
          <div class="flex items-center gap-2 text-xs">
            <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Present
            </span>
            <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
              <span class="w-2 h-2 rounded-full bg-rose-500"></span> Absent
            </span>
            <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              <span class="w-2 h-2 rounded-full bg-amber-500"></span> Late
            </span>
          </div>
        </div>
        <div class="relative h-64 sm:h-72 w-full">
          <canvas ref="lineChart" class="w-full h-full"></canvas>
        </div>
      </div>

      <!-- Working Hours Bar Chart -->
      <div class="bg-white dark:bg-[#111C30] rounded-2xl p-5 border border-slate-200/80 dark:border-[#263449] shadow-xs">
        <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <span>Working Time (Hours & Mins)</span>
          </h3>
          <span class="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-md border border-indigo-200/60 dark:border-indigo-900/60">
            Total Logged
          </span>
        </div>
        <div class="relative h-64 sm:h-72 w-full">
          <canvas ref="barChart" class="w-full h-full"></canvas>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449]"
    >
      <div class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-500 mb-3">
        <BarChart3 class="w-6 h-6" />
      </div>
      <p class="text-slate-600 dark:text-slate-300 text-sm font-semibold">
        No attendance records found for this period
      </p>
      <p class="text-xs text-slate-400 mt-1">
        Try selecting a different year or range to view logs
      </p>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, nextTick, onMounted, onUnmounted, computed } from "vue";
  import { Chart, registerables } from "chart.js/auto";
  import {
    CheckCircle2,
    XCircle,
    Clock,
    Briefcase,
    LogIn,
    LogOut,
    BarChart3,
  } from "lucide-vue-next";

  Chart.register(...registerables);

  const props = defineProps({
    userId: String,
    userName: String,
    selectedYear: Number,
    attendanceData: Object,
  });

  const emit = defineEmits(["update:selectedYear", "filter-change"]);

  const lineChart = ref(null);
  const barChart = ref(null);

  let lineChartInstance = null;
  let barChartInstance = null;

  const selectedYearLocal = ref(props.selectedYear || new Date().getFullYear());

  const dateRangeOption = ref("full");
  const startDate = ref("");
  const endDate = ref("");

  const currentYear = new Date().getFullYear();

  const isCurrentYear = computed(() => {
    return selectedYearLocal.value === currentYear;
  });

  const dateRangeOptions = computed(() => {
    if (isCurrentYear.value) {
      return [
        { label: "Full Year", value: "full" },
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last 7 Days", value: "last7days" },
        { label: "Last 30 Days", value: "last30days" },
        { label: "Custom Range", value: "custom" },
      ];
    }

    return [
      { label: "Full Year", value: "full" },
      { label: "Custom Range", value: "custom" },
    ];
  });

  watch(
    () => props.selectedYear,
    (val) => {
      selectedYearLocal.value = val;
    },
  );

  watch(selectedYearLocal, (val) => {
    emit("update:selectedYear", val);
    emitFilters();
  });

  watch([startDate, endDate], ([s, e]) => {
    if (dateRangeOption.value === "custom" && s && e) {
      emitFilters();
    }
  });

  function handleDateRangeChange() {
    const today = new Date();
    const format = (d) => d.toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today":
        startDate.value = format(today);
        endDate.value = format(today);
        break;

      case "yesterday": {
        const y = new Date();
        y.setDate(today.getDate() - 1);
        startDate.value = format(y);
        endDate.value = format(y);
        break;
      }

      case "last7days": {
        const past7 = new Date();
        past7.setDate(today.getDate() - 6);
        startDate.value = format(past7);
        endDate.value = format(today);
        break;
      }

      case "last30days": {
        const past30 = new Date();
        past30.setDate(today.getDate() - 29);
        startDate.value = format(past30);
        endDate.value = format(today);
        break;
      }

      default:
        startDate.value = "";
        endDate.value = "";
    }

    emitFilters();
  }

  function emitFilters() {
    const formatToMonthDay = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${month}-${day}`;
    };

    const payload = {
      year: selectedYearLocal.value,
      startDate: formatToMonthDay(startDate.value),
      endDate: formatToMonthDay(endDate.value),
    };

    emit("filter-change", payload);
  }

  watch(
    () => props.attendanceData,
    async (data) => {
      if (!data) return;
      await nextTick();
      if (lineChartInstance) lineChartInstance.destroy();
      if (barChartInstance) barChartInstance.destroy();
      updateCharts(data);
    },
  );

  function updateCharts(data) {
    const { monthlyStats = {}, dailyStats = {} } = data;

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let labels = [];
    let presentData = [];
    let absentData = [];
    let lateData = [];
    let workingMinutesData = [];

    if (dateRangeOption.value === "full") {
      labels = months;
      presentData = months.map((m) => monthlyStats[m]?.present ?? 0);
      absentData = months.map((m) => monthlyStats[m]?.absent ?? 0);
      lateData = months.map((m) => monthlyStats[m]?.late ?? 0);
      workingMinutesData = months.map(
        (m) => monthlyStats[m]?.workingMinutes ?? 0,
      );
    } else {
      const dates = [];
      const start = new Date(startDate.value);
      const end = new Date(endDate.value);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = new Date(d).toISOString().split("T")[0];
        dates.push(iso);
      }

      labels = dates.map((d) =>
        new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      );

      presentData = dates.map((d) => dailyStats[d]?.present ?? 0);
      absentData = dates.map((d) => dailyStats[d]?.absent ?? 0);
      lateData = dates.map((d) => dailyStats[d]?.late ?? 0);
      workingMinutesData = dates.map((d) => dailyStats[d]?.workingMinutes ?? 0);
    }

    const isDark = document.documentElement.classList.contains("dark");
    const tickColor = isDark ? "#94A3B8" : "#64748B";
    const gridColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)";

    // Chart 1: Stacked Attendance
    if (lineChart.value) {
      const ctx = lineChart.value.getContext("2d");

      lineChartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Present",
              data: presentData,
              backgroundColor: "#10B981",
              stack: "attendance",
              borderRadius: 5,
              maxBarThickness: 28,
            },
            {
              label: "Absent",
              data: absentData,
              backgroundColor: "#F43F5E",
              stack: "attendance",
              borderRadius: 5,
              maxBarThickness: 28,
            },
            {
              label: "Late",
              data: lateData,
              backgroundColor: "#F59E0B",
              stack: "attendance",
              borderRadius: 5,
              maxBarThickness: 28,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: isDark ? "rgba(17, 28, 48, 0.95)" : "rgba(15, 23, 42, 0.95)",
              titleColor: "#FFFFFF",
              bodyColor: "#CBD5E1",
              footerColor: "#38BDF8",
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)",
              borderWidth: 1,
              padding: 10,
              cornerRadius: 10,
              callbacks: {
                footer: (items) => {
                  const total = items.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
                  return `Total: ${total} records`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { color: tickColor, font: { size: 11, weight: "600" } },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: gridColor, borderDash: [4, 4] },
              ticks: { color: tickColor, font: { size: 10 }, stepSize: 1 },
              border: { display: false },
            },
          },
        },
      });
    }

    // Chart 2: Working Hours
    if (barChart.value) {
      const ctx = barChart.value.getContext("2d");

      // Gradient for working hours
      const gradient = ctx.createLinearGradient(0, 0, 0, 240);
      gradient.addColorStop(0, "#6366F1");
      gradient.addColorStop(1, "#8B5CF6");

      barChartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Working Time",
              data: workingMinutesData,
              backgroundColor: gradient,
              borderRadius: 6,
              maxBarThickness: 28,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: isDark ? "rgba(17, 28, 48, 0.95)" : "rgba(15, 23, 42, 0.95)",
              titleColor: "#FFFFFF",
              bodyColor: "#CBD5E1",
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)",
              borderWidth: 1,
              padding: 10,
              cornerRadius: 10,
              callbacks: {
                label(context) {
                  const minutes = context.raw || 0;
                  const h = Math.floor(minutes / 60);
                  const m = minutes % 60;
                  return `Total Worked: ${h}h ${m}m`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: tickColor, font: { size: 11, weight: "600" } },
            },
            y: {
              beginAtZero: true,
              grid: { color: gridColor, borderDash: [4, 4] },
              ticks: {
                color: tickColor,
                font: { size: 10 },
                callback(value) {
                  const h = Math.floor(value / 60);
                  return `${h}h`;
                },
              },
              border: { display: false },
            },
          },
        },
      });
    }
  }

  onMounted(() => {
    if (props.attendanceData) {
      updateCharts(props.attendanceData);
    }
  });

  onUnmounted(() => {
    if (lineChartInstance) lineChartInstance.destroy();
    if (barChartInstance) barChartInstance.destroy();
  });
</script>
